import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => { document.title = "Reset Password — Dream Home Plans"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password too short", { description: "Minimum 6 characters." });
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated", { description: "Sign in with your new password." });
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error("Reset failed", { description: err.response?.data?.detail || "Try requesting a new link." });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <div className="overline text-destructive">Missing token</div>
          <h1 className="font-display text-4xl mt-3">Invalid reset link</h1>
          <p className="mt-4 text-muted-foreground">This link is missing or malformed. Please request a new one.</p>
          <Link to="/forgot-password">
            <Button className="rounded-none bg-primary mt-8 h-12 px-8">Request a new link</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="overline text-accent">Password reset</div>
        <h1 className="font-display text-5xl mt-3 mb-8 leading-none tracking-tighter">Set a new password.</h1>

        {!done ? (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="overline text-muted-foreground">New password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none mt-2 h-12"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Confirm password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="rounded-none mt-2 h-12"
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full rounded-none h-12 bg-primary"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        ) : (
          <div className="text-center py-8" data-testid="reset-success">
            <CheckCircle2 size={40} className="mx-auto text-accent" />
            <div className="font-display text-3xl mt-4">Password updated</div>
            <p className="mt-3 text-muted-foreground">Redirecting to sign in…</p>
          </div>
        )}

        <Link to="/login" className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </div>
    </Layout>
  );
};

export default ResetPassword;
